import { useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys, useAccessToken } from "../../..";
import { configuration } from "../../../../configuration";
import { useSignalR } from "../../../../hooks";
import { CreateProposalDto, FetchError, GetIssuesDto, IssueDto, UserDto } from "../../../types";
import { optimisticallyUpdateQueryData } from "../../utilities";

type Variables = {
  proposalGuid: string;
  issueGuid: string;
  issueCreatedByUser: UserDto;
  description?: string;
  notes?: string;
  teamSlug?: string;
};

const useCreateProposalRequest = () => {
  const { getAccessToken } = useAccessToken();

  async function createProposalRequest(variables: Variables): Promise<void> {
    const accessToken = await getAccessToken();
    const body: CreateProposalDto = {
      proposalGuid: variables.proposalGuid,
      issueGuid: variables.issueGuid,
      description: variables.description,
      note: variables.notes,
    };

    const response = await fetch(`${configuration.apiRootUrl}/proposals`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      method: "post",
      body: JSON.stringify(body),
    });

    if (!response.ok) {
      throw new FetchError(response);
    }
  }

  return { createProposalRequest };
};

const useCreateProposal = () => {
  const { createProposalRequest } = useCreateProposalRequest();
  const queryClient = useQueryClient();
  const signalR = useSignalR();

  return useMutation({
    mutationFn: createProposalRequest,
    onMutate: async (variables) => {
      let cachedTeamIssuesData: GetIssuesDto | undefined;
      let cachedUserIssuesData: GetIssuesDto | undefined;
      let cachedIssueData: IssueDto | undefined;

      if (variables.teamSlug) {
        cachedTeamIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
          queryClient,
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          (cachedData) => ({
            ...cachedData,
            issues: cachedData.issues.map((issue) => {
              if (issue.guid === variables.issueGuid) {
                return {
                  ...issue,
                  proposalCount: issue.proposalCount + 1,
                };
              }
              return issue;
            }),
          }),
        );
      }

      cachedUserIssuesData = await optimisticallyUpdateQueryData<GetIssuesDto>(
        queryClient,
        queryKeys.issues.getIssues(undefined, variables.issueCreatedByUser.userId),
        (cachedData) => {
          if (!cachedData) return cachedData;
          return {
            ...cachedData,
            issues: cachedData.issues.map((issue) => {
              if (issue.guid === variables.issueGuid) {
                return {
                  ...issue,
                  proposalCount: issue.proposalCount + 1,
                };
              }
              return issue;
            }),
          };
        },
      );

      cachedIssueData = await optimisticallyUpdateQueryData<IssueDto>(
        queryClient,
        queryKeys.issues.getIssueDetail(variables.issueGuid),
        (cachedData) => ({
          ...cachedData,
          proposals: [
            ...cachedData.proposals,
            {
              guid: variables.proposalGuid,
              description: variables.description,
              note: variables.notes,
              selected: false,
              createdByUser: variables.issueCreatedByUser,
              upvotes: [],
            },
          ],
        }),
      );

      return { cachedTeamIssuesData, cachedUserIssuesData, cachedIssueData };
    },
    onSuccess: (_data, variables) => {
      if (variables.teamSlug) {
        signalR.sendRefetchTeamIssues(variables.teamSlug);
      }

      signalR.sendRefetchIssue(variables.issueGuid);
      signalR.sendRefetchUserIssues(variables.issueCreatedByUser.userId);
      signalR.sendRefetchComments(variables.issueGuid);
    },
    onError: (_error, variables, context) => {
      if (variables.teamSlug) {
        queryClient.setQueryData(
          queryKeys.issues.getIssues(variables.teamSlug, undefined),
          context?.cachedTeamIssuesData,
        );
      }

      queryClient.setQueryData(
        queryKeys.issues.getIssues(undefined, variables.issueCreatedByUser.userId),
        context?.cachedUserIssuesData,
      );
      queryClient.setQueryData(queryKeys.issues.getIssueDetail(variables.issueGuid), context?.cachedIssueData);
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries(queryKeys.issues.getIssues(variables.teamSlug, undefined));
      queryClient.invalidateQueries(queryKeys.issues.getIssues(undefined, variables.issueCreatedByUser.userId));
      queryClient.invalidateQueries(queryKeys.issues.getIssueDetail(variables.issueGuid));
      queryClient.invalidateQueries(queryKeys.issues.getIssueFeed(variables.issueGuid));
    },
  });
};

export { useCreateProposal };
